import {EventStreamContentType, fetchEventSource} from '@microsoft/fetch-event-source'
import type {CalculationResult, PackageCoverages} from 'backend/src/responses/calculation'
import {format} from 'date-fns'
import invariant from 'invariant'
import {
  isEmpty, isUndefined, omitBy, omit, partition, get, toUpper, isFunction, noop, set, first,
} from 'lodash-es'
import type {FC, MutableRefObject, ReactNode} from 'react'
import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'
import * as schemas from '../../../common/schemas'
import type {ExtendedCalculation} from '../../../common/schemas/calculations'
import type {CalculationUuid} from '../../../common/schemas/uuids'
import {ADDON_DEFAULT} from '../../../constants/defaultAddons'
import {FUEL_ELEKTRO} from '../../../constants/fuels'
import * as legalForms from '../../../constants/legalFormTypes'
import * as offers from '../../../constants/offers'
import {OFFER_PACKAGE} from '../../../constants/offers'
import type {PaymentFrequency} from '../../../constants/paymentFrequencies'
import * as paymentFrequencies from '../../../constants/paymentFrequencies'
import * as vehicleTypes from '../../../constants/vehicleTypes'
import {parseDamageClaims} from '../components/screens/form/components/DamageClaims'
import type {GeneralForm} from '../components/screens/form/utils'
import t from '../translations'
import {formatErrorMessage} from '../utils/errors'
import {getCompanyValue, getZipCodeOption, parseDate, validator} from '../utils/forms'
import {implementations, insurances} from '../utils/implementations'
import useAlert from './useAlert'
import useLoading from './useLoading'
import useStoredForm from './useStoredForm'
import type {StoredForm} from './useStoredForm'


export class CalculationError extends Error {
  status?: number
}

type OriginalPrices = {
  [period in PaymentFrequency]: {
    originalTerm: number
    originalTotal: number
  } | {
    originalTerm?: undefined
    originalTotal?: undefined
  }
}

export type Addon = Omit<CalculationResult & {
  offer: 'addon'
} & OriginalPrices, 'offer'>

export type Package = Omit<CalculationResult & {
  offer: 'package'
  addons: Addon[]
} & OriginalPrices, 'offer'>

export const validate = (passedValues: Record<string, unknown>) => {
  const result = validator(schemas.calculations.extendedCalculation)(passedValues)
  const errors = result.errors || {}

  return {values: result.values, errors}
}

export const formatValues = (values: StoredForm) => ({
  ...values,
  birthday: values.birthday ? format(new Date(values.birthday), 'dd.MM.yyyy') : undefined,
  zipCodeValue: values.zipCode && values.city
    ? getZipCodeOption({zipCode: values.zipCode, city: values.city}).value
    : values.zipCodeValue,
  companyValue: values.companyNumber && values.companyName
    ? getCompanyValue({companyNumber: values.companyNumber, companyName: values.companyName}).value
    : values.companyValue,
})

const parseVehicle = (values: Partial<GeneralForm>) => {
  const {vehicleType} = values

  if (!vehicleType) return {}

  switch (vehicleType) {
    case vehicleTypes.VEHICLE_TYPE_CAR_M1:
    case vehicleTypes.VEHICLE_TYPE_CAR_N1:
    case vehicleTypes.VEHICLE_TYPE_MOTORCYCLE:
    case vehicleTypes.VEHICLE_TYPE_TRAILER:
      return {
        licensePlate: values.licensePlate && !isEmpty(values.licensePlate)
          ? toUpper(values.licensePlate.replace(' ', ''))
          : undefined,
        vin: !isEmpty(values.vin) ? toUpper(values.vin) : undefined,
      }
    case vehicleTypes.VEHICLE_TYPE_SCOOTER:
      return {
        licensePlate: undefined,
        vin: !isEmpty(values.vin) ? toUpper(values.vin) : undefined,
        brand: undefined,
        model: undefined,
        volume: undefined,
        fuel: FUEL_ELEKTRO,
        registrationDate: undefined,
      }
    default:
      invariant(false, `Invalid vehicleType: ${vehicleType satisfies never}`)
  }
}

const parseLegalForm = (values: Partial<GeneralForm>) => {
  const {legalForm} = values

  if (!legalForm) return {}

  switch (legalForm) {
    case legalForms.PRIVATE_PERSON:
      return {
        birthday: values.birthday ? parseDate(values.birthday) : undefined,
        bornNumber: values.bornNumber ? values.bornNumber : undefined,
        companyNumber: undefined,
        companyName: undefined,
      }
    case legalForms.SELF_EMPLOYED:
      return {
        birthday: undefined,
        bornNumber: values.bornNumber ? values.bornNumber : undefined,
        ztp: undefined,
        unionUzpDiscount: undefined,
        companyValue: values.companyValue || undefined,
        companyName: values.companyName || undefined,
      }
    case legalForms.COMPANY:
      return {
        birthday: undefined,
        bornNumber: undefined,
        ztp: undefined,
        unionUzpDiscount: undefined,
        companyValue: values.companyValue || undefined,
        companyName: values.companyName || undefined,
      }
    default:
      invariant(false, `Invalid vehicleType: ${legalForm satisfies never}`)
  }
}

export const parseValues = (values: Partial<GeneralForm>) => {
  return {
    ...values,
    ...parseVehicle(values),
    ...parseLegalForm(values),
    ...parseDamageClaims(values),
  }
}

const updatePackages = (packages: Package[], offer: Extract<CalculationResult, {offer: 'package'}>) => {
  if (!insurances.has(offer.insurance) || !implementations.has(offer.implementation)) return packages

  const partitionedPackages = partition(packages, ({implementation, packageCoverage}) => {
    return implementation !== offer.implementation || packageCoverage !== offer.packageCoverage
  })

  const [packagesWithoutCurrent, packagesWithCurrent] = partitionedPackages
  const currentPackage = first(packagesWithCurrent) || null

  if (offer.yearly.pending || (currentPackage && !currentPackage.yearly.pending && offer.yearly.error)) {
    return packages
  }

  const formattedPackage = {
    ...omit(offer, ['offer']),
    addons: [],
  }

  set(
    formattedPackage,
    'yearly.originalTerm',
    currentPackage?.yearly.originalTerm || offer.yearly.term
  )
  set(
    formattedPackage,
    'yearly.originalTotal',
    currentPackage?.yearly.originalTotal || offer.yearly.total
  )

  set(
    formattedPackage,
    'semiAnnually.originalTerm',
    currentPackage?.semiAnnually.originalTerm || offer.semiAnnually.term
  )
  set(
    formattedPackage,
    'semiAnnually.originalTotal',
    currentPackage?.semiAnnually.originalTotal || offer.semiAnnually.total
  )

  set(
    formattedPackage,
    'quarterly.originalTerm',
    currentPackage?.quarterly.originalTerm || offer.quarterly.term
  )
  set(
    formattedPackage,
    'quarterly.originalTotal',
    currentPackage?.quarterly.originalTotal || offer.quarterly.total
  )

  set(
    formattedPackage,
    'addons',
    currentPackage?.addons ?? []
  )

  return [...packagesWithoutCurrent, formattedPackage] satisfies Package[]
}

type Values = ExtendedCalculation | {uuid: CalculationUuid}

export const getQueryString = (query: Values) => {
  const searchParams = omitBy(query, isUndefined)
  return !isEmpty(searchParams) ? `?${new window.URLSearchParams(searchParams).toString()}` : ''
}

const pendingResult = {pending: true as const}

const pendingResults = {
  [paymentFrequencies.PAYMENT_FREQUENCY_YEARLY]: pendingResult,
  [paymentFrequencies.PAYMENT_FREQUENCY_SEMIANNUALLY]: pendingResult,
  [paymentFrequencies.PAYMENT_FREQUENCY_QUARTERLY]: pendingResult,
}

const createPendingPackage = <
  I extends PackageCoverages['insurance'],
  V extends PackageCoverages['implementation'],
  PC extends PackageCoverages['packageCoverages'][number],
>({
    insurance,
    implementation,
    packageCoverage,
  }:{
  insurance: I,
  implementation: V,
  packageCoverage: PC
}) => {
  return {insurance, implementation, offer: OFFER_PACKAGE, addons: [], packageCoverage, ...pendingResults} as Package
}

const updateAddons = (packages: Package[], offer: Extract<CalculationResult, {offer: 'addon'}>) => {
  if (!insurances.has(offer.insurance) || !implementations.has(offer.implementation)) return packages

  const defaultAddonCoverage = get(ADDON_DEFAULT, [offer.implementation, offer.addon])
  if (!isEmpty(defaultAddonCoverage) && defaultAddonCoverage !== offer.addonCoverage) {
    return packages
  }

  const formattedAddon = omit(offer, ['offer', 'insurance', 'implementation', 'packageCoverage']) as Addon

  const partitionedPackages = partition(packages, ({insurance, packageCoverage}) => {
    return insurance !== offer.insurance || packageCoverage !== offer.packageCoverage
  })

  const [packagesWithoutCurrent, packagesWithCurrent] = partitionedPackages
  const currentPackage = first(packagesWithCurrent) || createPendingPackage(offer)


  if (offer.yearly.pending || (!currentPackage.yearly.pending && offer.yearly.error)) {
    return packages
  }

  const partitionedAddons = partition(currentPackage.addons, ({addon, addonCoverage}) => {
    return addon !== offer.addon || addonCoverage !== offer.addonCoverage
  })

  const [addonsWithoutCurrent, addonsWithCurrent] = partitionedAddons
  const currentAddon = first(addonsWithCurrent) || null

  set(
    formattedAddon,
    'yearly.originalTerm',
    currentAddon?.yearly.originalTerm || offer.yearly.term
  )
  set(
    formattedAddon,
    'yearly.originalTotal',
    currentAddon?.yearly.originalTotal || offer.yearly.total
  )

  set(
    formattedAddon,
    'semiAnnually.originalTerm',
    currentAddon?.semiAnnually.originalTerm || offer.semiAnnually.term
  )
  set(
    formattedAddon,
    'semiAnnually.originalTotal',
    currentAddon?.semiAnnually.originalTotal || offer.semiAnnually.total
  )

  set(
    formattedAddon,
    'quarterly.originalTerm',
    currentAddon?.quarterly.originalTerm || offer.quarterly.term
  )
  set(
    formattedAddon,
    'quarterly.originalTotal',
    currentAddon?.quarterly.originalTotal || offer.quarterly.total
  )

  currentPackage.addons = [...addonsWithoutCurrent, formattedAddon] as const
  return [...packagesWithoutCurrent, currentPackage] satisfies Package[]
}

type Options = {
  reset?: boolean
  signal?: AbortSignal
  onError?: (error: Error, calculationUuid: CalculationUuid | null) => void
  onOpen?: (res: Response, calculationUuid: CalculationUuid) => void
  onPackage?: (offer: CalculationResult) => void
  onClose?: (calculationUuid: CalculationUuid | null) => void
}

type CalculateContextType = {
  packages: Package[]
  calculationUuid: CalculationUuid | null
  isFetching: boolean
  calculate: (values: Values, options?: Options) => Promise<void>
  onAddon: MutableRefObject<(offer: CalculationResult) => void>
  error: Error | null
}

const CalculateContext = createContext<CalculateContextType | null>(null)

type CalculateProviderProps = {
  children: ReactNode
}

export const CalculateProvider: FC<CalculateProviderProps> = ({children}) => {
  const [packages, setPackages] = useState<Package[]>([])
  const {resetForm} = useStoredForm()
  const {setLoading} = useLoading()
  const {showAlert} = useAlert()
  const [isFetching, setFetching] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [calculationUuid, setCalculationUuid] = useState<CalculationUuid | null>(null)
  const onAddon = useRef(noop)

  const calculate = useCallback(async (values: Values, options: Options = {}) => {
    const search = getQueryString(values)
    if (options.reset) {
      setCalculationUuid(null)
      setPackages([])
    }
    setFetching(true)
    setError(null)

    // eslint-disable-next-line no-process-env
    return fetchEventSource(`${process.env.GATSBY_API_URL}/api/calculate/sse${search}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'text/event-stream',
      },
      openWhenHidden: true,
      signal: options.signal,
      onopen: async (res) => {
        if (!res.ok || !(res.headers.get('content-type') === EventStreamContentType)) {
          const resultError = await res.json()
          const error = new CalculationError(res.statusText || 'Unknown error', {cause: resultError})
          setError(error)
          if (options.onError) {
            options.onError(error, null)
          }
          error.status = res.status
          throw error
        }
        const calculationUuid = res.headers.get('x-calculation-uuid') as CalculationUuid | null

        invariant(calculationUuid, 'Calculation UUID must be present in successful response')

        setCalculationUuid(calculationUuid)

        if (options.onOpen) {
          options.onOpen(res, calculationUuid)
        }
      },
      onmessage: (event) => {
        const offer = JSON.parse(event.data) as CalculationResult

        if (!offer || isEmpty(offer)) return

        if (offer.offer === offers.OFFER_PACKAGE) {
          if (options.onPackage) {
            options.onPackage(offer)
          }
          setPackages((packages) => updatePackages(packages, offer))
        }
        if (offer.offer === offers.OFFER_ADDON) {
          if (onAddon.current) {
            onAddon.current(offer)
          }
          setPackages((packages) => updateAddons(packages, offer))
        }
      },
      onclose: () => {
        setFetching(false)
        if (options.onClose) {
          options.onClose(calculationUuid)
        }
      },
      onerror: (err) => {
        setError(err)
        if (options.onError) {
          options.onError(err, calculationUuid)
        }
        // Unrecoverable error
        if (err.status >= 400 && (err.status < 500 || err.status === 503) && err.status !== 429) {
          resetForm()
          setFetching(false)
          showAlert('uuid' in values ? t('form.errorGlobal') : formatErrorMessage(err.cause, values), {
            buttonText: t('form.errorButton'),
          })
          setLoading(false)
          throw err
        }
      },
    })
  }, [calculationUuid, setLoading, onAddon, resetForm, showAlert])

  const contextValue = useMemo(() => ({
    packages,
    calculationUuid,
    isFetching,
    calculate,
    onAddon,
    error,
  }), [packages, calculationUuid, calculate, isFetching, error])

  return (
    <CalculateContext.Provider value={contextValue}>
      {children}
    </CalculateContext.Provider>
  )
}

type UseCalculateOptions = {
  onAddon?: (offer: Extract<CalculationResult, {offer: 'addon'}>) => void
}

const useCalculate = ({onAddon}:UseCalculateOptions = {}) => {
  const ctx = useContext(CalculateContext)

  invariant(ctx, 'useCalculate must be used within a CalculateProvider')

  const {calculate, calculationUuid, onAddon: onAddonRef, isFetching, error} = ctx

  useEffect(() => {
    if (isFunction(onAddon)) {
      onAddonRef.current = onAddon
    }

    return () => {
      onAddonRef.current = noop
    }
  }, [onAddonRef, onAddon])

  const handleCalculate = useCallback(async (values: Values, options: Options = {}) => {
    return await calculate(values, options)
  }, [calculate])

  return {calculate: handleCalculate, calculationUuid, isFetching, error}
}

export const usePackages = () => {
  const ctx = useContext(CalculateContext)
  invariant(ctx, 'usePackages must be used within a CalculateProvider')
  return ctx.packages
}

export default useCalculate
